import React from 'react';
import { useLanguage } from './LanguageContext';

const Pagination = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100]
}) => {
  const { print_lang } = useLanguage();

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  if (totalCount === 0) {
    return null;
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem 0',
      borderTop: '1px solid var(--line)',
      marginTop: '1rem',
      flexWrap: 'wrap',
      gap: '1rem'
    }}>
      {/* Left: Items info */}
      <div style={{ fontSize: '0.875rem', color: 'var(--n-500)' }}>
        {print_lang('pagination.showing', 'Showing')} {startItem}-{endItem} {print_lang('pagination.of', 'of')} {totalCount} {print_lang('pagination.items', 'items')}
      </div>

      {/* Center: Page navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        {/* First Page */}
        <button
          className="btn btn-sm btn-outline"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          title={print_lang('pagination.first', 'First')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="11 17 6 12 11 7" />
            <polyline points="18 17 13 12 18 7" />
          </svg>
        </button>

        {/* Previous Page */}
        <button
          className="btn btn-sm btn-outline"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          title={print_lang('pagination.previous', 'Previous')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Page Numbers */}
        {getPageNumbers()[0] > 1 && (
          <>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => onPageChange(1)}
            >
              1
            </button>
            {getPageNumbers()[0] > 2 && (
              <span style={{ padding: '0 0.5rem', color: 'var(--n-500)' }}>...</span>
            )}
          </>
        )}

        {getPageNumbers().map(page => (
          <button
            key={page}
            className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => onPageChange(page)}
            style={{ minWidth: '2rem' }}
          >
            {page}
          </button>
        ))}

        {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
          <>
            {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
              <span style={{ padding: '0 0.5rem', color: 'var(--n-500)' }}>...</span>
            )}
            <button
              className="btn btn-sm btn-outline"
              onClick={() => onPageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}

        {/* Next Page */}
        <button
          className="btn btn-sm btn-outline"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          title={print_lang('pagination.next', 'Next')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {/* Last Page */}
        <button
          className="btn btn-sm btn-outline"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          title={print_lang('pagination.last', 'Last')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="13 17 18 12 13 7" />
            <polyline points="6 17 11 12 6 7" />
          </svg>
        </button>
      </div>

      {/* Right: Page size selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--n-500)' }}>
          {print_lang('pagination.perPage', 'Per page')}:
        </span>
        <select
          className="form-select"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          style={{ width: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default Pagination;
